import { type ReactNode, useEffect, useRef, useState } from "react";
import { XStack, YStack } from "tamagui";

import { RXQuestionContentReplySuggestionType as ReplyType } from "@/api/generated/models";
import {
  useSendLocationInput,
  useSendRxInput,
} from "@/api/mutations/conversation";
import { ConstrainedView, Skeleton } from "@/components";
import { useAskUserQuestionContainerStyles } from "@/conversation/AskUserQuestionsPanel";
import {
  type ChoiceValue,
  OtherTextInput,
  PencilBadge,
  QuestionScrollArea,
} from "@/conversation/AskUserQuestionsPanel/OptionParts";
import PanelHeader from "@/conversation/AskUserQuestionsPanel/PanelHeader";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";
import DropdownQuestion from "@/conversation/AssistantMessage/AskUserQuestions/DropdownQuestion";
import MultipleChoiceQuestion from "@/conversation/AssistantMessage/AskUserQuestions/MultipleChoiceQuestion";
import RxPhotoUpload from "@/conversation/AssistantMessage/AskUserQuestions/RxPhotoUpload";
import SingleChoiceQuestion from "@/conversation/AssistantMessage/AskUserQuestions/SingleChoiceQuestion";
import HeightWeightInput from "@/conversation/HeightWeightInput";
import { useCurrentConversationId } from "@/hooks/conversation";
import { useRxSurvey } from "@/hooks/useRxSurvey";

const EMPTY_CHOICE: ChoiceValue = { selected: [], otherText: "" };
// A BE-provided option that opens a free-text "specify" row, mapped to the shared components' Other row.
const OTHER_OPTION = "Other";

const noop = () => {};

// These scroll their own options so their submit controls stay pinned below the list.
const SELF_SCROLLING_REPLY_TYPES = new Set<ReplyType | undefined>([
  ReplyType.multiple_choice_single,
  ReplyType.multiple_choice_multiple,
]);

type CurrentQuestion = {
  question: string;
  replyType: ReplyType | undefined;
  options: string[];
};

const answerFromChoice = (choice: ChoiceValue, multiple: boolean): string => {
  const parts = [...choice.selected];
  if (choice.otherText.trim()) parts.push(choice.otherText.trim());
  return multiple ? parts.join(", ") : (parts[0] ?? "");
};

/**
 * The interactive body for one rx question. Remounted per question (keyed on question id) so its
 * local answer state resets. Choice/dropdown answers submit through the shared ask-user components;
 * free-text questions answer in an inline text box here.
 */
function RxQuestionInteraction({
  currentQuestion,
  isLocation,
  conversationId,
  onWaiting,
}: {
  currentQuestion: CurrentQuestion;
  isLocation: boolean;
  conversationId: string;
  onWaiting: () => void;
}) {
  const { sendRxInput } = useSendRxInput(conversationId);
  const { sendLocationInput } = useSendLocationInput(conversationId);

  const [choice, setChoice] = useState<ChoiceValue>(EMPTY_CHOICE);
  const choiceRef = useRef<ChoiceValue>(EMPTY_CHOICE);
  const onChange = (value: ChoiceValue) => {
    choiceRef.current = value;
    setChoice(value);
  };

  const sendInput = (message: string) => {
    if (isLocation) {
      sendLocationInput(message);
    } else {
      sendRxInput(message);
    }
  };

  const submit = (multiple: boolean) => {
    const answer = answerFromChoice(choiceRef.current, multiple);
    if (!answer) return;
    onWaiting();
    sendInput(answer);
  };

  const hasOther = currentQuestion.options.includes(OTHER_OPTION);
  const visibleOptions = hasOther
    ? currentQuestion.options.filter((option) => option !== OTHER_OPTION)
    : currentQuestion.options;

  switch (currentQuestion.replyType) {
    case ReplyType.photo_upload:
      return (
        <RxPhotoUpload
          conversationId={conversationId}
          onWaiting={onWaiting}
          prompt={currentQuestion.question}
        />
      );
    case ReplyType.height_weight:
      return (
        <HeightWeightInput
          onSubmit={({ displayText, heightIn, weightLb }) => {
            onWaiting();
            sendRxInput(displayText, {
              height_in: heightIn,
              weight_lb: weightLb,
            });
          }}
        />
      );
    case ReplyType.single_select_dropdown:
      return (
        <DropdownQuestion
          options={currentQuestion.options}
          value={choice}
          onChange={onChange}
          onSubmit={() => submit(false)}
          onSkip={noop}
          isLast={false}
          allowSkip={false}
        />
      );
    case ReplyType.multiple_choice_single:
      return (
        <SingleChoiceQuestion
          options={visibleOptions}
          value={choice}
          onChange={onChange}
          onSubmit={() => submit(false)}
          onSkip={noop}
          isLast={false}
          allowOther={hasOther}
          allowSkip={false}
          dataTrackingId="rx-questionnaire-option"
        />
      );
    case ReplyType.multiple_choice_multiple:
      return (
        <MultipleChoiceQuestion
          options={visibleOptions}
          value={choice}
          onChange={onChange}
          onSubmit={() => submit(true)}
          onSkip={noop}
          isLast={false}
          allowOther={hasOther}
          allowSkip={false}
          dataTrackingId="rx-questionnaire-option"
        />
      );
    default: {
      // Free text: answered inline here on a single text row with a submit arrow.
      return (
        <XStack
          alignItems="center"
          gap="$200"
          paddingHorizontal="$150"
          paddingBottom="$150"
        >
          <XStack flex={1} alignItems="center" gap="$100">
            <PencilBadge />
            <OtherTextInput
              value={choice.otherText}
              onChangeText={(otherText) =>
                onChange({ selected: [], otherText })
              }
              onSubmitEditing={() => submit(false)}
            />
          </XStack>
          <SkipOrSubmit
            showSkip={false}
            showSubmit={choice.otherText.trim().length > 0}
            isLast
            onSkip={noop}
            onSubmit={() => submit(false)}
          />
        </XStack>
      );
    }
  }
}

function QuestionSkeleton() {
  return (
    <YStack gap="$200" paddingHorizontal="$150" paddingBottom="$200">
      <Skeleton.Text lines={2} widths={["100%", "85%"]} />
      <YStack gap="$100">
        <Skeleton height="$500" borderRadius="$200" />
        <Skeleton height="$500" borderRadius="$200" />
        <Skeleton height="$500" borderRadius="$200" />
      </YStack>
    </YStack>
  );
}

const SurveyPanel = ({ conversationId }: { conversationId: string }) => {
  const survey = useRxSurvey(conversationId);
  const containerStyles = useAskUserQuestionContainerStyles();

  // After submitting an answer the panel waits for the next card; the skeleton shows until the step
  // advances. `step` is the active question id.
  const [waiting, setWaiting] = useState(false);
  const step = survey.activeQuestionId ?? "";
  const lastStepRef = useRef(step);
  useEffect(() => {
    if (step !== lastStepRef.current) {
      lastStepRef.current = step;
      setWaiting(false);
    }
  }, [step]);

  if (!survey.isActive) return null;
  // The clinical-ai-v1-rx flow has no consent card — it opens on the first question.
  if (survey.isAtConsent) return null;

  let body: ReactNode;
  if (waiting) {
    body = <QuestionSkeleton />;
  } else if (survey.activeQuestion) {
    const content = survey.activeQuestion.content;
    const currentQuestion: CurrentQuestion = {
      question: content.next_question ?? "",
      replyType: content.reply_suggestion_type,
      options: content.reply_suggestions || [],
    };
    // The photo card swaps through its own sub-states (upload/confirm/pick/no-match), so it renders its
    // own per-state header rather than this one fixed prompt.
    const isPhoto = currentQuestion.replyType === ReplyType.photo_upload;
    const interaction = (
      <RxQuestionInteraction
        key={survey.activeQuestion.id}
        currentQuestion={currentQuestion}
        isLocation={survey.activeQuestion.isLocation}
        conversationId={conversationId}
        onWaiting={() => setWaiting(true)}
      />
    );
    body = (
      <>
        {!isPhoto && (
          <PanelHeader
            question={currentQuestion.question}
            index={0}
            total={1}
            onPrev={noop}
            onNext={noop}
          />
        )}
        {SELF_SCROLLING_REPLY_TYPES.has(currentQuestion.replyType) ? (
          interaction
        ) : (
          <QuestionScrollArea>{interaction}</QuestionScrollArea>
        )}
      </>
    );
  } else {
    body = <QuestionSkeleton />;
  }

  return (
    <ConstrainedView>
      <YStack {...containerStyles}>{body}</YStack>
    </ConstrainedView>
  );
};

export default function RxSurveyPanel() {
  const conversationId = useCurrentConversationId();
  if (!conversationId) return null;
  return <SurveyPanel conversationId={conversationId} />;
}
